import React, { useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
    IconButton,
    makeStyles,
    Switch,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import { AddressForm, AddressState } from '../../components'
import { INVALID_PHONE_MESSAGE, isInvalidPhoneError } from '../../helpers'

interface QuickAddVendorModalProps {
    isOpen: boolean
    onClose: () => void
    onVendorCreated: (vendorId: number) => void
    createVendor: (body: QuickVendorData) => Promise<number>
    isCreating: boolean
}

export interface QuickVendorData {
    name: string
    email: string
    phone?: string
    address: string
    city: string
    state: string
    zip_code: string
    in_house?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: theme.spacing(1.5),
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    iconContainer: {
        height: 44,
        width: 44,
        borderRadius: '50%',
        backgroundColor: theme.palette.success.light + '20',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.success.main,
    },
    title: {
        fontSize: '1.125rem',
        fontWeight: 600,
    },
    content: {
        padding: theme.spacing(3),
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    fieldRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(2),
    },
    fieldRowThree: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr',
        gap: theme.spacing(2),
    },
    actions: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
}))

const defaultAddressState: AddressState = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
}

export const QuickAddVendorModal: React.FC<QuickAddVendorModalProps> = ({
    isOpen,
    onClose,
    onVendorCreated,
    createVendor,
    isCreating,
}) => {
    const classes = useStyles()

    // Form state
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [inHouse, setInHouse] = useState(false)
    const [addressState, setAddressState] = useState<AddressState>(
        defaultAddressState,
    )
    const [doValidate, setDoValidate] = useState(false)
    const [phoneError, setPhoneError] = useState(false)

    const resetForm = () => {
        setName('')
        setEmail('')
        setPhone('')
        setInHouse(false)
        setAddressState(defaultAddressState)
        setDoValidate(false)
        setPhoneError(false)
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    const handleCreate = async () => {
        setDoValidate(true)

        if (!isFormValid) return

        const vendorData: QuickVendorData = {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim() || undefined,
            address: addressState.addressLine1.trim(),
            city: addressState.city.trim(),
            state: addressState.state.trim(),
            zip_code: addressState.zip.trim(),
            in_house: inHouse,
        }

        try {
            const vendorId = await createVendor(vendorData)
            onVendorCreated(vendorId)
            handleClose()
        } catch (error) {
            if (isInvalidPhoneError(error)) {
                setPhoneError(true)
            }
        }
    }

    const isFormValid =
        name.trim() !== '' &&
        email.trim() !== '' &&
        addressState.addressLine1.trim() !== '' &&
        addressState.city.trim() !== '' &&
        addressState.state.trim() !== '' &&
        addressState.zip.trim() !== ''

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ className: classes.paper }}
        >
            {/* Header */}
            <Box className={classes.header}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.iconContainer}>
                        <PersonAddIcon />
                    </Box>
                    <Box>
                        <Typography className={classes.title}>
                            Add New Vendor
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Create a new vendor to assign to this contract
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    disabled={isCreating}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent className={classes.content}>
                <Box className={classes.form}>
                    <TextField
                        label="Vendor Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        required
                        error={doValidate && name === ''}
                        helperText={doValidate && name === '' ? 'Required' : ''}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isCreating}
                    />

                    <Box className={classes.fieldRow}>
                        <TextField
                            label="Email"
                            variant="outlined"
                            size="small"
                            type="email"
                            fullWidth
                            required
                            error={doValidate && email === ''}
                            helperText={
                                doValidate && email === '' ? 'Required' : ''
                            }
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isCreating}
                        />

                        <TextField
                            label="Phone"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={phone}
                            onChange={(e) => {
                                setPhone(e.target.value)
                                setPhoneError(false)
                            }}
                            error={phoneError}
                            helperText={phoneError ? INVALID_PHONE_MESSAGE : ''}
                            disabled={isCreating}
                        />
                    </Box>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={inHouse}
                                color="primary"
                                onChange={() => setInHouse(!inHouse)}
                                disabled={isCreating}
                            />
                        }
                        label="In House"
                    />

                    <AddressForm
                        doValidate={doValidate}
                        addressState={addressState}
                        setAddressState={setAddressState}
                        inputStyle={{ marginBottom: 0 }}
                    />
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions className={classes.actions}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    color="default"
                    disabled={isCreating}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    color="primary"
                    disabled={isCreating || !isFormValid}
                    startIcon={
                        isCreating ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : null
                    }
                >
                    {isCreating ? 'Creating...' : 'Create Vendor'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
